import React, { useState, useEffect, useCallback } from 'react';
import { FiPackage, FiGrid, FiList, FiPlus, FiDownload, FiUpload } from 'react-icons/fi';
import jsPDF from 'jspdf';
import InventoryList from '../components/inventory/InventoryList';
import InventoryForm from '../components/inventory/InventoryForm';
import InventoryFilters from '../components/inventory/InventoryFilters';
import InventoryStats from '../components/inventory/InventoryStats';
import ConfirmModal from '../components/Modals/ConfirmModal';
import { inventoryAPI, inventoryCategoryAPI } from '../utils/inventoryApi';
import { apiCall } from '../utils/apiHelpers';
import { authFetch } from '../utils/api';
import autoTable from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import '../styles/inventory.css';
import CustomToast from '../components/common/CustomToast';

const InventoryPage = () => {
  const { t } = useTranslation();
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    priceRange: { min: '', max: '' },
    stockRange: { min: '', max: '' }
  });
  const [viewMode, setViewMode] = useState('table');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, itemId: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [serverStats, setServerStats] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await inventoryCategoryAPI.getCategories();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // categories failure is non-critical, no toast needed
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiCall(authFetch, '/inventory/stats', {
        method: 'GET'
      });
      const data = response?.data || response;
      if (data) {
        setServerStats(data);
      }
    } catch (error) {
      console.warn('Error fetching inventory stats:', error);
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query string from filters
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.priceRange.min) queryParams.append('priceRange.min', filters.priceRange.min);
      if (filters.priceRange.max) queryParams.append('priceRange.max', filters.priceRange.max);
      if (filters.stockRange.min) queryParams.append('stockRange.min', filters.stockRange.min);
      if (filters.stockRange.max) queryParams.append('stockRange.max', filters.stockRange.max);

      const queryString = queryParams.toString();
      const url = queryString ? `/inventory?${queryString}` : '/inventory';

      console.log('Fetching inventory from:', url);
      console.log('Filters:', filters);

      const response = await apiCall(authFetch, url, {
        method: 'GET'
      });

      console.log('Inventory response received:', response);

      // Extract data from the response object based on backend structure
      // Backend returns: { success: true, data: [...], total: 10 }
      const data = response?.data || response || [];
      console.log('Inventory data:', data);

      // Ensure we always set an array
      setInventory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setError('Failed to fetch inventory');
      setInventory([]);
      setToast({
        show: true,
        message: 'Failed to load inventory items',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Listen for inventory updates from other components
  useEffect(() => {
    const handleInventoryUpdate = (event) => {
      console.log('🔄 Inventory update event received:', event.detail);
      fetchInventory(); // Refresh inventory when update event is fired
      fetchStats(); // Refresh sales, expenses and profit/loss stats
    };

    window.addEventListener('inventoryUpdated', handleInventoryUpdate);

    return () => {
      window.removeEventListener('inventoryUpdated', handleInventoryUpdate);
    };
  }, [fetchInventory, fetchStats]);

  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, [fetchCategories, fetchStats]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleAddItem = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteItem = (itemId) => {
    setConfirmDelete({ open: true, itemId });
  };

  const confirmDeleteItem = async () => {
    try {
      const response = await apiCall(authFetch, `/inventory/${confirmDelete.itemId}`, {
        method: 'DELETE'
      });

      console.log('Delete response:', response);

      setInventory(prev => prev.filter(item => item.id !== confirmDelete.itemId));
      setConfirmDelete({ open: false, itemId: null });
      fetchStats();
      setToast({
        show: true,
        message: 'Item deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      setToast({
        show: true,
        message: 'Failed to delete item',
        type: 'error'
      });
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      let response;

      if (editingItem) {
        // Update existing item
        response = await apiCall(authFetch, `/inventory/${editingItem.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemData)
        });
      } else {
        // Create new item
        response = await apiCall(authFetch, '/inventory', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemData)
        });
      }

      console.log('Save response:', response);

      // Extract data from response (backend returns { success: true, data: {...}, message: '...' })
      const savedItem = response?.data || response;

      if (editingItem) {
        // Update the item in the list
        setInventory(prev => prev.map(item =>
          item.id === editingItem.id ? savedItem : item
        ));
        setToast({
          show: true,
          message: 'Item updated successfully',
          type: 'success'
        });
      } else {
        // Add new item to the list
        setInventory(prev => [savedItem, ...prev]);
        setToast({
          show: true,
          message: 'Item created successfully',
          type: 'success'
        });
      }

      setShowModal(false);
      setEditingItem(null);
      fetchStats();
    } catch (error) {
      console.error('Error saving item:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to save item',
        type: 'error'
      });
      // Re-throw to let form know (if needed), but we handle toast here
    }
  };

  const handleCategoryAdd = async (categoryData) => {
    try {
      const newCategory = await inventoryCategoryAPI.createCategory(categoryData);
      setCategories(prev => [...prev, newCategory]);

      // If we are in the form, ensuring the new category is available is handled by passing updated categories prop
      // But we might want to also toast success
      setToast({
        show: true,
        message: 'Category added successfully',
        type: 'success'
      });

      return newCategory; // Return for the modal to potentially use
    } catch (error) {
      console.error('Error adding category:', error);
      setToast({
        show: true,
        message: 'Failed to add category',
        type: 'error'
      });
      throw error;
    }
  };


  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleDownload = () => {
    if (!inventory || inventory.length === 0) {
      setToast({
        show: true,
        message: 'No inventory to download',
        type: 'error'
      });
      return;
    }

    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text('Inventory Report', 14, 22);

    // Add date
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 30);

    // Define table columns and rows
    const tableColumn = ["Item Code", "Name", "Category", "HSN", "Stock", "Cost Price", "Selling Price", "Status"];
    const tableRows = [];

    inventory.forEach(item => {
      const cost = Number(item.cost_price) || 0;
      const selling = Number(item.selling_price) || 0;
      const rowData = [
        item.public_id || item.sku || '',
        item.name || '',
        item.category?.name || item.category_name || '',
        item.hsn || '',
        item.quantity || 0,
        `Rs. ${cost.toFixed(2)}`,
        `Rs. ${selling.toFixed(2)}`,
        item.status || ''
      ];
      tableRows.push(rowData);
    });

    // Generate table
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [102, 126, 234] } // Matches brand color
    });

    // Save PDF
    doc.save(`inventory_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleHsnUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const newCodes = [];
      lines.forEach(line => {
        const parts = line.split(',');
        if (parts.length >= 2) {
          const code = parts[0].trim();
          const label = parts.slice(1).join(',').trim();
          if (code && label && code !== 'Code') { // basic check to ignore header
            newCodes.push({ code, label });
          }
        }
      });
      if (newCodes.length > 0) {
        const existing = localStorage.getItem('custom_hsn_codes');
        const existingCodes = existing ? JSON.parse(existing) : [];
        const combined = [...existingCodes, ...newCodes];
        
        // Remove duplicates
        const uniqueOptions = Array.from(new Map(combined.map(item => [item.code, item])).values());
        
        localStorage.setItem('custom_hsn_codes', JSON.stringify(uniqueOptions));
        setToast({ show: true, message: `Successfully imported ${newCodes.length} custom HSN codes!`, type: 'success' });
      } else {
        setToast({ show: true, message: 'No valid codes found. Use "Code,Description" format.', type: 'error' });
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };


  return (
    <div className="inventory-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <FiPackage />
          </div>
          <div>
            <h1>{t('inventory.title', 'Inventory Management')}</h1>
            <p>{t('inventory.subtitle', 'Manage your product inventory, track stock levels, and monitor product performance')}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleAddItem}>
            <FiPlus /> {t('inventory.addItem', 'Add Item')}
          </button>
          <label className="btn btn-secondary hsn-import-label">
            <FiUpload /> Import HSN
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleHsnUpload} />
          </label>
        </div>
      </div>

      {/* Stats Cards (Sales, Expenses, Profit & Loss, Valuation & Stock Health) */}
      <InventoryStats inventory={inventory} serverStats={serverStats} />

      {/* Controls Section */}
      <div className="controls-section">
        <InventoryFilters
          filters={filters}
          onFiltersChange={setFilters}
          categories={categories}
        />
        <div className="view-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={handleDownload} disabled={loading || inventory.length === 0}>
            <FiDownload /> {t('inventory.download', 'Download')}
          </button>
          <div style={{ width: '1px', height: '24px', backgroundColor: '#e2e8f0', margin: '0 4px' }}></div>
          <button
            className={`btn ${viewMode === 'table' ? 'btn-primary' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <FiList /> {t('inventory.table', 'Table')}
          </button>
          <button
            className={`btn ${viewMode === 'grid' ? 'btn-primary' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <FiGrid /> {t('inventory.grid', 'Grid')}
          </button>
        </div>
      </div>

      {/* Inventory List */}
      <InventoryList
        inventory={inventory}
        loading={loading}
        error={error}
        viewMode={viewMode}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onDownload={handleDownload}
      />

      {/* Add/Edit Modal */}
      <InventoryForm
        show={showModal}
        onClose={handleCloseModal}
        onSave={handleSaveItem}
        editingItem={editingItem}
        categories={categories}
        onCategoryAdd={handleCategoryAdd}
        inventory={inventory}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, itemId: null })}
        onConfirm={confirmDeleteItem}
        title={t('inventory.deleteTitle', 'Delete Item')}
        message={t('inventory.deleteMessage', 'Are you sure you want to delete this item? This action cannot be undone.')}
        confirmText={t('inventory.delete', 'Delete')}
        cancelText={t('inventory.cancel', 'Cancel')}
      />

      <CustomToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default InventoryPage;